package com.fantasytenis.web.components.bracket

import androidx.compose.runtime.Composable
import com.fantasytenis.web.theme.Colors
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Floating controls overlay for the infinite canvas bracket.
 *
 * Provides:
 * - Zoom in/out buttons
 * - Fit-to-screen button
 * - Current zoom level indicator
 */
@Composable
fun CanvasControls(
    zoom: Double,
    onZoomIn: () -> Unit,
    onZoomOut: () -> Unit,
    onFitToScreen: () -> Unit
) {
    // Container positioned in bottom-right
    Div({
        classes("canvas-controls")
        style {
            position(Position.Absolute)
            bottom(20.px)
            right(20.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(8.px)
            property("z-index", "100")
            property("pointer-events", "auto")
        }
    }) {
        // Zoom level indicator
        Div({
            style {
                backgroundColor(Colors.Surface)
                borderRadius(6.px)
                padding(6.px, 10.px)
                textAlign("center")
                property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.15)")
            }
        }) {
            Span({
                style {
                    fontSize(12.px)
                    fontWeight(500)
                    color(Colors.TextSecondary)
                }
            }) {
                Text("${(zoom * 100).toInt()}%")
            }
        }

        // Zoom buttons group
        Div({
            style {
                backgroundColor(Colors.Surface)
                borderRadius(8.px)
                overflow("hidden")
                property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.15)")
            }
        }) {
            // Zoom in
            ControlButton(
                icon = "+",
                title = "Zoom in (+)",
                onClick = onZoomIn,
                isTopButton = true
            )

            // Divider
            Div({
                style {
                    height(1.px)
                    backgroundColor(Colors.Border)
                }
            })

            // Zoom out
            ControlButton(
                icon = "-",
                title = "Zoom out (-)",
                onClick = onZoomOut,
                isTopButton = false
            )
        }

        // Fit to screen button
        Div({
            style {
                backgroundColor(Colors.Surface)
                borderRadius(8.px)
                overflow("hidden")
                property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.15)")
            }
        }) {
            ControlButton(
                icon = "\u26F6", // Square with corners icon (or use custom SVG)
                title = "Fit to screen (0)",
                onClick = onFitToScreen,
                isTopButton = true
            )
        }

        // Keyboard shortcuts hint
        Div({
            style {
                backgroundColor(Color("rgba(0, 0, 0, 0.6)"))
                borderRadius(6.px)
                padding(6.px, 8.px)
                marginTop(4.px)
            }
        }) {
            Span({
                style {
                    fontSize(10.px)
                    color(Colors.White)
                    opacity(0.8)
                }
            }) {
                Text("Scroll: zoom | Drag: pan")
            }
        }
    }
}

/**
 * Individual control button.
 */
@Composable
private fun ControlButton(
    icon: String,
    title: String,
    onClick: () -> Unit,
    isTopButton: Boolean
) {
    Div({
        style {
            width(40.px)
            height(40.px)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            cursor("pointer")
            backgroundColor(Colors.Surface)
            property("transition", "background-color 0.15s ease")
            if (isTopButton) {
                borderRadius(8.px, 8.px, 0.px, 0.px)
            } else {
                borderRadius(0.px, 0.px, 8.px, 8.px)
            }
        }
        attr("title", title)
        onClick { onClick() }
        onMouseEnter {
            it.currentTarget.asDynamic().style.backgroundColor = Colors.Gray100.toString()
        }
        onMouseLeave {
            it.currentTarget.asDynamic().style.backgroundColor = Colors.Surface.toString()
        }
    }) {
        Span({
            style {
                fontSize(18.px)
                fontWeight(500)
                color(Colors.TextPrimary)
                property("user-select", "none")
            }
        }) {
            Text(icon)
        }
    }
}

/**
 * Extended canvas controls with navigation features.
 * Includes quick-jump buttons for large brackets.
 */
@Composable
fun CanvasControlsExtended(
    zoom: Double,
    drawSize: Int,
    onZoomIn: () -> Unit,
    onZoomOut: () -> Unit,
    onFitToScreen: () -> Unit,
    onJumpToFinal: () -> Unit,
    onJumpToStart: () -> Unit
) {
    Div({
        classes("canvas-controls-extended")
        style {
            position(Position.Absolute)
            bottom(20.px)
            right(20.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(8.px)
            property("z-index", "100")
        }
    }) {
        // Navigation buttons (for large brackets)
        if (drawSize >= 64) {
            Div({
                style {
                    backgroundColor(Colors.Surface)
                    borderRadius(8.px)
                    overflow("hidden")
                    property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.15)")
                    marginBottom(4.px)
                }
            }) {
                NavigationButton(
                    label = "Final",
                    icon = "\uD83C\uDFC6",
                    onClick = onJumpToFinal
                )

                Div({
                    style {
                        height(1.px)
                        backgroundColor(Colors.Border)
                    }
                })

                NavigationButton(
                    label = "R1",
                    icon = "\u2190",
                    onClick = onJumpToStart
                )
            }
        }

        // Standard zoom controls
        CanvasControls(
            zoom = zoom,
            onZoomIn = onZoomIn,
            onZoomOut = onZoomOut,
            onFitToScreen = onFitToScreen
        )
    }
}

/**
 * Navigation quick-jump button.
 */
@Composable
private fun NavigationButton(
    label: String,
    icon: String,
    onClick: () -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(6.px)
            padding(8.px, 12.px)
            cursor("pointer")
            backgroundColor(Colors.Surface)
            property("transition", "background-color 0.15s ease")
        }
        onClick { onClick() }
        onMouseEnter {
            it.currentTarget.asDynamic().style.backgroundColor = Colors.Gray100.toString()
        }
        onMouseLeave {
            it.currentTarget.asDynamic().style.backgroundColor = Colors.Surface.toString()
        }
    }) {
        Span({
            style {
                fontSize(14.px)
            }
        }) {
            Text(icon)
        }
        Span({
            style {
                fontSize(12.px)
                fontWeight(500)
                color(Colors.TextPrimary)
            }
        }) {
            Text(label)
        }
    }
}

// NOTE: Minimap component can be added in a future iteration if needed.
// It would display a scaled-down view of the bracket with a draggable viewport rectangle.
